import { TRACKING_PAGE } from '@repo/tracking/tracker-listener';
import { useTracking } from '@repo/tracking/use-tracking';
import { useTranslation } from '@repo/translations/use-translation';
import { useEffect, useRef } from 'react';

import { Alert } from '@zooplus/ui-components';

import { useCustomerOverview } from '../../../hooks/useCustomerOverview';
import { useDelayedCondition } from '../../../hooks/useDelayedCondition';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useMembershipPremiumOffer } from '../../../hooks/useMembershipOffer';
import { useMembershipsComparison } from '../../../hooks/useMembershipsComparison';
import { FAQSection } from '../../FAQSection/FAQSection';
import { PremiumBannerLandingPage } from '../PremiumBannerLandingPage/PremiumBannerLandingPage';
import { ProgramBenefitsAdvertisement } from '../program-benefits/ProgramBenefitsAdvertisement/ProgramBenefitsAdvertisement';
import { ProgramBenefitsSummary } from '../program-benefits/ProgramBenefitsSummary/ProgramBenefitsSummary';
import { ProgramPlan } from '../program-plan/ProgramPlan/ProgramPlan';
import { Banner } from '../welcome-banner/Banner/Banner';

import styles from './LandingPage.module.css';

import type { CustomerOverview } from '@repo/types/customer-overview';
import type { MembershipOfferInfo } from '@repo/types/membership-offer';

export interface LandingPageProps {
  isCustomerLoggedIn: boolean;
}

export const LandingPage = ({ isCustomerLoggedIn }: LandingPageProps) => {
  const { t } = useTranslation();
  useDocumentTitle(t('loyalty-program.program-name'));
  const { data: membership, error: membershipError } =
    useMembershipPremiumOffer(isCustomerLoggedIn);
  const { data: customerOverview, error: customerOverviewError } =
    useCustomerOverview(isCustomerLoggedIn);
  const { data: membershipsComparison } = useMembershipsComparison();
  const { trackPageView } = useTracking();

  // show the error message only if the error condition persists for more than 3 seconds
  const hasError = useDelayedCondition(customerOverviewError || membershipError, 3000);

  const eventAlreadySent = useRef(false);
  useEffect(() => {
    if (!eventAlreadySent.current) {
      trackPageView({
        page: TRACKING_PAGE.LOYALTY_PROGRAM_LANDING_PAGE,
      });
      eventAlreadySent.current = true;
    }
  }, [trackPageView]);

  const renderLandingPageContent = (
    isCustomerLoggedIn: boolean,
    customerOverview?: CustomerOverview,
  ) => (
    <>
      <Banner
        isCustomerLoggedIn={isCustomerLoggedIn}
        customerInfo={customerOverview?.customerInfo}
        customerType={customerOverview?.customerType}
      />
      <ProgramBenefitsSummary />
      {membershipsComparison && (
        <ProgramPlan
          membershipsComparison={membershipsComparison}
          isBasicCustomer={customerOverview ? customerOverview.customerType === 'basic' : false}
        />
      )}
      <ProgramBenefitsAdvertisement />
    </>
  );

  const renderLoggedInContent = (
    customerOverview: CustomerOverview,
    membership?: MembershipOfferInfo,
  ) => {
    return (
      <div className={styles.landingPageContainer}>
        {renderLandingPageContent(true, customerOverview)}
        {customerOverview.customerType === 'basic' && membership && (
          <PremiumBannerLandingPage offer={membership.offer} />
        )}
        <div className={styles.faqWrapper}>
          <FAQSection />
        </div>
      </div>
    );
  };

  const renderLoggedOutContent = () => (
    <div className={styles.landingPageContainer}>
      {renderLandingPageContent(false)}
      <div className={styles.faqWrapper}>
        <FAQSection />
      </div>
    </div>
  );

  if (isCustomerLoggedIn) {
    if (hasError) {
      return (
        <Alert variant="error" data-testid="error-message" className={styles.alert}>
          {t('loyalty-program.error-alert-message')}
        </Alert>
      );
    }

    if (customerOverview) {
      return renderLoggedInContent(customerOverview, membership);
    }
  } else {
    return renderLoggedOutContent();
  }

  return null;
};
